import { useCallback, useEffect, useRef, useState } from 'react';
import { useFlowchartConnection } from '../../FlowchartView/hooks/useFlowchartConnection';
import { useClipboard } from '../../FlowchartView/hooks/useClipboard';
import { computeEdgeAutoPanDelta } from '../../FlowchartView/hooks/utils/canvasUtils';
import { place } from '../utils/placeCamera';
import { paintConnectionLine } from '../../FlowchartView/components/EdgeRenderer/ConnectionPreview';

function nodeUnderPoint(tasks, positions, dimensions, fromId, x, y) {
  let best = null;
  let bestArea = Infinity;
  tasks.forEach((task) => {
    if (!task?.id || task.id === fromId) return;
    const pos = positions[task.id];
    const dims = dimensions[task.id];
    if (!pos || !dims) return;
    const inside = x >= pos.x && x <= pos.x + dims.width && y >= pos.y && y <= pos.y + dims.height;
    if (!inside) return;
    const area = dims.width * dims.height;
    if (area < bestArea) {
      bestArea = area;
      best = task.id;
    }
  });
  return best;
}

/** Where a (+) card first appears: on the handle, so tidy can slide it out from there. */
function spawnBeside(graph, sourceTaskId, side) {
  const pos = graph?.nodePositions?.[sourceTaskId];
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return null;
  const height = graph?.nodeDimensions?.[sourceTaskId]?.height || 0;
  return {
    x: pos.x,
    y: side === 'before' ? pos.y - 28 : pos.y + Math.max(height - 28, 28),
  };
}

function screenToCanvas(rect, camera, clientX, clientY) {
  const zoom = camera.zoom || 1;
  return {
    x: (clientX - rect.left - camera.x) / zoom,
    y: (clientY - rect.top - camera.y) / zoom,
  };
}

/**
 * Draw a line onto another mission, or add one from a handle.
 * The board lays the cards out again when the tasks change.
 */
export function useBoardConnections({
  tasks,
  graph,
  selectedIds,
  wrapRef,
  worldRef,
  cameraRef,
  flowRef,
  gestureRef,
  onSelectionChangeRef,
  tidyFocusRef,
  tidySettledRef,
}) {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const pointerRef = useRef(null);
  const endRef = useRef({ x: 0, y: 0 });
  const lineRef = useRef(null);
  const boundsRef = useRef(null);
  const moveListenerRef = useRef(null);
  const completeRef = useRef(() => {});
  const setSelectedNodes = useCallback((ids) => {
    onSelectionChangeRef.current?.(ids instanceof Set ? ids : new Set(ids));
  }, [onSelectionChangeRef]);

  const onConditionsChange = useCallback((taskId, conditions, options) => (
    flowRef.current?.onConditionsChange?.(taskId, conditions, options)
  ), [flowRef]);

  const onPasteTasks = useCallback((payload) => (
    flowRef.current?.onPasteTasks?.(payload)
  ), [flowRef]);

  const onDeleteTasks = useCallback((ids, options) => (
    flowRef.current?.onDeleteTasks?.(ids, options)
  ), [flowRef]);

  const tasksLiveRef = useRef(tasks);
  tasksLiveRef.current = tasks;

  const onConnectionComplete = useCallback((_sourceId, targetId, bundle) => {
    if (tidyFocusRef) tidyFocusRef.current = targetId;
    const record = bundle?.undoDefer?.recordCombinedAction;
    if (record && tidySettledRef) {
      tidySettledRef.current = () => {
        tidySettledRef.current = null;
        record(bundle.originalPositions, tasksLiveRef.current);
      };
    }
  }, [tidyFocusRef, tidySettledRef]);

  const connection = useFlowchartConnection({
    tasks,
    nodePositions: graph.nodePositions,
    nodeDimensions: graph.nodeDimensions,
    customPositions: {},
    onConditionsChange,
    onConnectionComplete,
    clearSelection: () => setSelectedNodes(new Set()),
  });
  completeRef.current = connection.completeConnection;
  const cancelOnlyRef = useRef(connection.cancelConnection);
  cancelOnlyRef.current = connection.cancelConnection;
  const fromRef = useRef(connection.connectingFrom);
  fromRef.current = connection.connectingFrom;
  const graphRef = useRef(graph);
  graphRef.current = graph;
  const updateTargetRef = useRef(connection.updateConnectionTarget);
  const cancelRef = useRef(connection.cancelConnection);
  updateTargetRef.current = connection.updateConnectionTarget;
  cancelRef.current = connection.cancelConnection;

  const clipboard = useClipboard({
    tasks,
    selectedNodes: selectedIds,
    setSelectedNodes,
    nodePositions: graph.nodePositions,
    nestedSprints: flowRef.current?.nestedSprints || [],
    allTags: flowRef.current?.allTags || [],
    customFields: flowRef.current?.customFields || [],
    onPasteTasks,
    onDeleteTasks,
    isMultiCampaignMode: !!flowRef.current?.isMultiCampaignMode,
    openCampaignPickerForPaste: flowRef.current?.openCampaignPickerForPaste,
    loadFullSprint: flowRef.current?.loadFullSprint,
    allSprints: flowRef.current?.allSprints || [],
  });

  const visitNewTask = useCallback((taskId) => {
    setSelectedNodes(new Set([taskId]));
    // Center on the layout destination, not the handle the card slides out of.
    if (tidyFocusRef) tidyFocusRef.current = taskId;
  }, [setSelectedNodes, tidyFocusRef]);

  const startConnection = useCallback((taskId, event, branch, startPos) => {
    pointerRef.current = { x: event.clientX, y: event.clientY };
    if (gestureRef) gestureRef.current = true;
    const wrap = wrapRef.current;
    if (wrap) {
      boundsRef.current = wrap.getBoundingClientRect();
      const canvas = screenToCanvas(boundsRef.current, cameraRef.current, event.clientX, event.clientY);
      endRef.current = canvas;
      setMousePos(canvas);
    }
    const onMove = (moveEvent) => {
      pointerRef.current = { x: moveEvent.clientX, y: moveEvent.clientY };
      const rect = boundsRef.current;
      if (!rect) return;
      const canvas = screenToCanvas(rect, cameraRef.current, moveEvent.clientX, moveEvent.clientY);
      endRef.current = canvas;
      const line = lineRef.current;
      if (line) paintConnectionLine(line.path, line.tip, line.startX, line.startY, canvas.x, canvas.y);
    };
    moveListenerRef.current = onMove;
    window.addEventListener('mousemove', onMove);
    connection.handleConnectionDragStart(taskId, event, branch, startPos);
    const onUp = (upEvent) => {
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mousemove', onMove);
      if (moveListenerRef.current === onMove) moveListenerRef.current = null;
      if (gestureRef) gestureRef.current = false;
      const wrap = wrapRef.current;
      const picture = graphRef.current;
      if (!wrap || !picture) {
        cancelOnlyRef.current();
        return;
      }
      const canvas = screenToCanvas(wrap.getBoundingClientRect(), cameraRef.current, upEvent.clientX, upEvent.clientY);
      const targetId = nodeUnderPoint(
        tasksLiveRef.current,
        picture.nodePositions,
        picture.nodeDimensions,
        fromRef.current,
        canvas.x,
        canvas.y,
      );
      if (targetId) completeRef.current(targetId);
      else cancelOnlyRef.current();
    };
    window.addEventListener('mouseup', onUp);
  }, [cameraRef, connection, gestureRef, wrapRef]);

  useEffect(() => {
    if (!connection.isConnecting) return undefined;
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    let rect = wrap.getBoundingClientRect();
    boundsRef.current = rect;
    let frame = 0;

    let seenX = pointerRef.current?.x;
    let seenY = pointerRef.current?.y;

    const paintAtPointer = () => {
      const pointer = pointerRef.current;
      const rect = boundsRef.current;
      if (!pointer || !rect) return null;
      const canvas = screenToCanvas(rect, cameraRef.current, pointer.x, pointer.y);
      endRef.current = canvas;
      const line = lineRef.current;
      if (line) paintConnectionLine(line.path, line.tip, line.startX, line.startY, canvas.x, canvas.y);
      return canvas;
    };

    const tick = () => {
      const pointer = pointerRef.current;
      if (pointer) {
        const delta = computeEdgeAutoPanDelta(pointer.x, pointer.y, rect);
        const panned = !!(delta.x || delta.y);
        if (panned) {
          const cam = cameraRef.current;
          cameraRef.current = { ...cam, x: cam.x + delta.x, y: cam.y + delta.y };
          place(worldRef.current, cameraRef.current);
          paintAtPointer();
        }
        if (panned || pointer.x !== seenX || pointer.y !== seenY) {
          seenX = pointer.x;
          seenY = pointer.y;
          updateTargetRef.current(endRef.current.x, endRef.current.y);
        }
      }
      frame = requestAnimationFrame(tick);
    };
    const onResize = () => {
      rect = wrap.getBoundingClientRect();
      boundsRef.current = rect;
    };
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (gestureRef) gestureRef.current = false;
      cancelRef.current();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      if (moveListenerRef.current) {
        window.removeEventListener('mousemove', moveListenerRef.current);
        moveListenerRef.current = null;
      }
      if (gestureRef) gestureRef.current = false;
    };
  }, [connection.isConnecting, cameraRef, gestureRef, worldRef, wrapRef]);

  const createConnected = useCallback((sourceTaskId, nodeType, branch) => {
    const taskId = `task-${Date.now()}`;
    const position = spawnBeside(graphRef.current, sourceTaskId, 'after');
    flowRef.current?.onCreateConnectedTask?.(taskId, sourceTaskId, nodeType, position, branch);
    visitNewTask(taskId);
  }, [flowRef, visitNewTask]);

  const insertBefore = useCallback((sourceTaskId, nodeType) => {
    const position = spawnBeside(graphRef.current, sourceTaskId, 'before');
    const taskId = flowRef.current?.onInsertMissionBefore?.(sourceTaskId, nodeType, position);
    if (taskId) visitNewTask(taskId);
  }, [flowRef, visitNewTask]);

  const insertOnEdge = useCallback((edge, nodeType, midpoint) => {
    if (nodeType === 'milestone') {
      flowRef.current?.onInsertMilestoneOnEdge?.(edge, midpoint);
      return;
    }
    const taskId = `task-${Date.now()}`;
    flowRef.current?.onInsertNodeOnEdge?.(taskId, edge.from, edge.to, edge.branch, nodeType, {
      x: midpoint.x - 145,
      y: midpoint.y - 75,
    });
    visitNewTask(taskId);
  }, [flowRef, visitNewTask]);

  return {
    mousePos,
    endRef,
    lineRef,
    isConnecting: connection.isConnecting,
    connectingFrom: connection.connectingFrom,
    connectingTarget: connection.connectingTarget,
    connectingBranch: connection.connectingBranch,
    connectingStartPos: connection.connectingStartPos,
    startConnection,
    createConnected,
    insertBefore,
    insertOnEdge,
    pasteBefore: clipboard.pasteBeforeHost,
    pasteAtPosition: clipboard.pasteAtPosition,
    hasClipboardData: clipboard.hasClipboardData,
    copyNode: clipboard.copyNode,
    duplicateNode: clipboard.duplicateNode,
  };
}
